import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Eye, Maximize2, Shuffle } from 'lucide-react'
import {
  DESIGNED_TEMPLATES,
  INVITE_TEMPLATES,
  cleanTemplateId,
  filterTemplates,
  getTemplate,
  isListed,
} from '../lib/inviteTemplates'
import type { TemplateCategory } from '../lib/inviteTemplates'
import { INVITE_FONTS, isDarkColor, loadInviteFont } from '../lib/inviteStyle'
import TemplateThumb, { sampleWordsFor } from './invite/TemplateThumb'
import ColourPicker from './invite/ColourPicker'
import PaintedColourRows from './invite/PaintedColourRows'
import PaintedPreviewDialog from './invite/PaintedPreviewDialog'
import TemplateFilterBar, { styleFilter } from './invite/TemplateFilterBar'
import type { TemplateStyle } from './invite/TemplateFilterBar'
import type { PaintedWords } from './invite/PaintedCard'

/**
 * The Design step's invitation designer.
 *
 * A template is a finished design — its own paper, ink, artwork and type. On
 * top of it the host may override one accent colour, one background colour and
 * one font pairing; each override repaints the whole invitation.
 *
 * With a hundred listed templates the chooser is a filtered grid under one
 * filter row (TemplateFilterBar: search over labels, blurbs and tags in both
 * languages, a Style menu — painted and its looks, or classic, whichever the
 * library lists — and a Category menu), showing static thumbnails
 * (TemplateThumb — no iframes). Painted
 * thumbnails carry the host's own title and date, and each
 * opens a large animated quick preview (PaintedPreviewDialog). A painted
 * template's rows offer the colours its own art suggests, and the background
 * only where the art is printed on a flat paper (PaintedColourRows); every
 * flat-colour row ends in the wildcard picker (invite/ColourPicker), so any
 * colour at all is one tap away. The
 * pickers always show the *template's own* choice as selected while the host
 * has not overridden anything, so the step opens on the design's own
 * recommendation instead of on "nothing picked". Choosing that same value
 * clears the override rather than storing it — the template then keeps its
 * hand-tuned gradients and artwork, which a flat colour cannot reproduce.
 *
 * The live preview of the real invitation is not part of the picker: the page
 * keeps it beside the designer (InvitePreviewFrame), so it stays in view while
 * the host scrolls through templates, colours and fonts.
 */

export type InviteDesign = {
  template?: string
  accent?: string
  background?: string
  font?: string
}

type Props = {
  value: InviteDesign
  onChange: (patch: InviteDesign) => void
  appLang?: string
  /** The invitation's own language, so the thumbnails' copy matches the event. */
  invLang?: string
  /** The host's title, date, time and venue, set on the painted cards. */
  words?: PaintedWords
}

// Accent colours offered alongside the template's own. Chosen to stay legible
// as type: each one has to work as the event name and as button fill.
const ACCENT_SWATCHES = [
  '#ff4d8d', '#be185d', '#8b5cf6', '#2563eb', '#0ea5a4', '#d97706', '#1b1033',
]

// Papers. The last three are dark on purpose — the invitation flips its ink,
// hairlines and frosted panels when the paper is dark (see .inv-bg-dark).
const BACKGROUND_SWATCHES = [
  '#ffffff', '#fdf6ec', '#f3ede4', '#eaf0ec', '#f1e7f4', '#e7eef7',
  '#2b2438', '#1f2a37', '#14110f',
]

const InviteTemplatePicker = ({ value, onChange, appLang = 'en', invLang = 'en', words }: Props) => {
  const t = (en: string, ka: string) => (appLang === 'ka' ? ka : en)
  const selected = cleanTemplateId(value.template)
  const template = getTemplate(selected)
  const defaults = template.defaults

  // What each picker shows as chosen: the host's override if there is one,
  // otherwise the template's own value.
  const accent = value.accent || defaults.accent
  const background = value.background || defaults.background
  const font = value.font || defaults.font

  // Storing the template's own value would swap the design's tuned gradients
  // for a flat fill that looks the same only in the swatch. Selecting the
  // default therefore clears the override instead.
  const setAccent = (v: string) =>
    onChange({ accent: v.toLowerCase() === defaults.accent.toLowerCase() ? '' : v })
  const setBackground = (v: string) =>
    onChange({ background: v.toLowerCase() === defaults.background.toLowerCase() ? '' : v })
  const setFont = (v: string) => onChange({ font: v === defaults.font ? '' : v })

  // ---- the library filter
  // Open where the current choice lives, so an existing event's template is
  // visible and highlighted. An event still on Auto, or on a design the
  // website no longer lists, opens on All — and the unlisted design stays
  // pinned first while nothing narrows the list, so the host can see and
  // preview what the event wears.
  const pinned = isListed(template) ? null : template
  const [style, setStyle] = useState<TemplateStyle>(() => (selected === 'auto' || pinned ? 'all' : template.collection))
  const [category, setCategory] = useState<TemplateCategory | 'all'>('all')
  const [query, setQuery] = useState('')
  const [quickId, setQuickId] = useState<string | null>(null)
  const visible = useMemo(() => {
    const listed = filterTemplates({ query, category, ...styleFilter(style) })
    return pinned && style === 'all' && !query && category === 'all' ? [pinned, ...listed] : listed
  }, [query, category, style, pinned])
  const hostWords = words?.title?.trim() ? words : null

  // Keep the chosen card in view when the step opens or the filter changes.
  const gridRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-template-id="${selected}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selected, visible])

  // The chips render in the faces they name, so load every pairing's webfont.
  useEffect(() => {
    INVITE_FONTS.forEach((f) => loadInviteFont(f.id))
  }, [])

  /**
   * Shuffle — a whole random look in one tap: template, accent, paper and
   * type, drawn from the entire library. It never lands on what is already
   * showing, so the button always visibly does something.
   */
  const shuffle = useCallback(() => {
    const pick = <T,>(arr: readonly T[]) => arr[Math.floor(Math.random() * arr.length)]
    const current = `${selected}|${value.accent || ''}|${value.background || ''}|${value.font || ''}`

    for (let i = 0; i < 12; i += 1) {
      const tpl = pick(filterTemplates(styleFilter(style)).filter((x) => x.id !== 'auto'))
      // Most of the time keep the design's own palette — the templates are the
      // best-looking combinations we have, and shuffle should surface them.
      const keepPalette = Math.random() < 0.7
      const pd = tpl.painted
      const bgChoices = pd ? [...(pd.papers || []), ...(pd.colorways || []).map((c) => c.swatch)] : BACKGROUND_SWATCHES
      const nextAccent = keepPalette ? '' : pick(pd ? pd.accents : ACCENT_SWATCHES)
      const nextBg = keepPalette || !bgChoices.length ? '' : pick(bgChoices)
      const nextFont = Math.random() < 0.7 ? '' : pick(INVITE_FONTS).id

      const patch = {
        template: tpl.id,
        accent: nextAccent === tpl.defaults.accent ? '' : nextAccent,
        background: nextBg === tpl.defaults.background ? '' : nextBg,
        font: nextFont === tpl.defaults.font ? '' : nextFont,
      }
      const key = `${patch.template}|${patch.accent}|${patch.background}|${patch.font}`
      if (key !== current) {
        onChange(patch)
        return
      }
    }
  }, [selected, value.accent, value.background, value.font, onChange, style])

  const rowLabel = 'mb-2 flex items-center gap-2 text-xs font-medium text-[#1b1033]/60 dark:text-gray-400'
  const resetBtn =
    'ml-1 text-[11px] font-medium text-[#4a3d66] underline underline-offset-2 hover:text-[#ff4d8d] dark:text-[#c3b8dd]'

  const swatchClass = (on: boolean) =>
    `h-9 w-9 rounded-full border transition-all ${
      on
        ? 'ring-2 ring-offset-2 ring-[#1b1033]/40 dark:ring-white/80 ring-offset-[#fff7f2] dark:ring-offset-[#130b26]'
        : 'border-black/10 hover:scale-105 dark:border-white/20'
    }`

  return (
    <div data-testid="invite-template-picker" className="space-y-5">
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Eye className="h-4 w-4 shrink-0 text-[#ff4d8d]" />
          <p className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400">
            {t('Invitation template', 'მოწვევის შაბლონი')}
          </p>
          <span className="hidden text-[11px] text-[#4a3d66] sm:inline dark:text-[#c3b8dd]/80">
            {t(`— ${DESIGNED_TEMPLATES.length} designs, the look guests open`, `— ${DESIGNED_TEMPLATES.length} დიზაინი, იერსახე, რომელსაც სტუმრები ნახავენ`)}
          </span>
          <button
            type="button"
            onClick={shuffle}
            className="ml-auto inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-[#ff4d8d]/35 bg-[#ff4d8d]/[0.06] px-3 text-xs font-semibold text-[#ff4d8d] transition-colors hover:border-[#ff4d8d] hover:bg-[#ff4d8d]/[0.12]"
            data-testid="invite-shuffle-btn"
            title={t('Random design', 'შემთხვევითი დიზაინი')}
          >
            <Shuffle className="h-3.5 w-3.5" />
            {t('Shuffle', 'შემთხვევითი')}
          </button>
        </div>

        <div className="mb-3">
          <TemplateFilterBar
            query={query}
            onQuery={setQuery}
            style={style}
            onStyle={setStyle}
            category={category}
            onCategory={setCategory}
            appLang={appLang}
            testPrefix="invite-template"
          />
        </div>

        {/* The grid of thumbnails, scrolling inside the step */}
        <div
          ref={gridRef}
          className="grid max-h-[560px] grid-cols-2 items-start gap-2 overflow-y-auto rounded-[4px] p-0.5 pr-1.5 sm:grid-cols-3 md:grid-cols-4 xl:max-h-[640px]"
          data-testid="invite-template-grid"
        >
          {visible.map((tpl) => {
            const on = tpl.id === selected
            return (
              <div key={tpl.id} className="group relative">
              <button
                type="button"
                onClick={() => onChange({ template: tpl.id })}
                aria-pressed={on}
                data-testid={`invite-template-${tpl.id}`}
                data-template-id={tpl.id}
                className={`relative flex w-full flex-col rounded-[6px] border p-1.5 text-left transition-all ${
                  on
                    ? 'border-[#ff4d8d] bg-[#ff4d8d]/[0.06] shadow-celebrate-md'
                    : 'border-[#1b1033]/12 hover:border-[#1b1033]/25 dark:border-white/10 dark:hover:border-white/25'
                }`}
              >
                {tpl.id === 'auto' ? (
                  <span
                    className="flex aspect-[4/5] w-full items-center justify-center rounded-[10px] ring-1 ring-black/5"
                    style={{ background: tpl.swatch.paper }}
                  >
                    <span
                      className="h-7 w-7 rounded-full"
                      style={{ background: tpl.swatch.accent, boxShadow: `inset 0 0 0 3px ${tpl.swatch.ink}` }}
                    />
                  </span>
                ) : (
                  <TemplateThumb
                    template={tpl}
                    lang={invLang}
                    className="w-full"
                    words={hostWords}
                    accent={on ? value.accent : null}
                    background={on ? value.background : null}
                  />
                )}
                <span className="mt-1.5 block truncate px-0.5 text-[11px] font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                  {appLang === 'ka' ? tpl.label.ka : tpl.label.en}
                </span>
                <span className="block px-0.5 text-[10px] leading-snug text-[#4a3d66] dark:text-[#c3b8dd]/80 line-clamp-2">
                  {appLang === 'ka' ? tpl.blurb.ka : tpl.blurb.en}
                </span>
                {on && (
                  <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#ff4d8d] text-white shadow">
                    <Check className="h-3 w-3" />
                  </span>
                )}
              </button>
              {tpl.painted && (
                <button
                  type="button"
                  onClick={() => setQuickId(tpl.id)}
                  aria-label={t(`Preview ${tpl.label.en}`, `${tpl.label.ka} — გადახედვა`)}
                  className="absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-full bg-[#1b1033]/70 px-2 py-1 text-[10px] font-medium text-white backdrop-blur transition-opacity hover:bg-[#1b1033] focus-visible:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                  data-testid={`invite-template-preview-${tpl.id}`}
                >
                  <Maximize2 className="h-3 w-3" />
                  {t('Preview', 'გადახედვა')}
                </button>
              )}
              </div>
            )
          })}
          {!visible.length && (
            <p className="col-span-full py-8 text-center text-xs text-[#4a3d66] dark:text-[#c3b8dd]/80" data-testid="invite-template-empty">
              {t('No template matches that — try another word.', 'ასეთი შაბლონი ვერ მოიძებნა — სცადე სხვა სიტყვა.')}
            </p>
          )}
        </div>
      </div>

      {template.painted ? (
        <PaintedColourRows
          def={template.painted}
          accent={value.accent}
          background={value.background}
          onAccent={(v) => onChange({ accent: v })}
          onBackground={(v) => onChange({ background: v })}
          appLang={appLang}
          testPrefix="invite-painted"
        />
      ) : (
      <>
      {/* Accent colour — the event name, the buttons and every accent mark. */}
      <div>
        <p className={rowLabel}>
          {t('Colour', 'ფერი')}
          <span className="font-normal text-[#4a3d66] dark:text-[#c3b8dd]/80">
            {value.accent
              ? t('— your colour', '— შენი ფერი')
              : t('— from the template', '— შაბლონის ფერი')}
          </span>
          {value.accent && (
            <button
              type="button"
              onClick={() => onChange({ accent: '' })}
              className={resetBtn}
              data-testid="accent-color-reset"
            >
              {t('Use template', 'შაბლონისა')}
            </button>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onChange({ accent: '' })}
            aria-pressed={!value.accent}
            title={t('Template colour', 'შაბლონის ფერი')}
            className={swatchClass(!value.accent)}
            style={{ backgroundColor: defaults.accent }}
            data-testid="accent-color-default"
          />
          <span className="mx-0.5 h-6 w-px bg-[#1b1033]/10 dark:bg-white/15" />
          {ACCENT_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setAccent(c)}
              aria-label={c}
              aria-pressed={accent.toLowerCase() === c}
              className={swatchClass(accent.toLowerCase() === c)}
              style={{ backgroundColor: c }}
              data-testid={`accent-color-${c}`}
            />
          ))}
          <ColourPicker
            value={accent}
            onChange={setAccent}
            active={!!value.accent && !ACCENT_SWATCHES.includes(accent.toLowerCase())}
            label={{ en: 'type', ka: 'ტექსტის' }}
            appLang={appLang}
            testid="accent-color-custom"
          />
        </div>
      </div>

      {/* Background colour — the paper the whole invitation is printed on. */}
      <div>
        <p className={rowLabel}>
          {t('Background', 'ფონი')}
          <span className="font-normal text-[#4a3d66] dark:text-[#c3b8dd]/80">
            {value.background
              ? isDarkColor(background)
                ? t('— dark paper', '— მუქი ფონი')
                : t('— your paper', '— შენი ფონი')
              : t('— from the template', '— შაბლონის ფონი')}
          </span>
          {value.background && (
            <button
              type="button"
              onClick={() => onChange({ background: '' })}
              className={resetBtn}
              data-testid="background-color-reset"
            >
              {t('Use template', 'შაბლონისა')}
            </button>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onChange({ background: '' })}
            aria-pressed={!value.background}
            title={t('Template background', 'შაბლონის ფონი')}
            className={swatchClass(!value.background)}
            style={{ backgroundColor: defaults.background }}
            data-testid="background-color-default"
          />
          <span className="mx-0.5 h-6 w-px bg-[#1b1033]/10 dark:bg-white/15" />
          {BACKGROUND_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setBackground(c)}
              aria-label={c}
              aria-pressed={background.toLowerCase() === c}
              className={swatchClass(background.toLowerCase() === c)}
              style={{ backgroundColor: c }}
              data-testid={`background-color-${c}`}
            />
          ))}
          <ColourPicker
            value={background}
            onChange={setBackground}
            active={!!value.background && !BACKGROUND_SWATCHES.includes(background.toLowerCase())}
            label={{ en: 'paper', ka: 'ქაღალდის' }}
            appLang={appLang}
            testid="background-color-custom"
          />
        </div>
      </div>
      </>
      )}

      {/* Font pairing — one choice drives the name, the copy and the buttons. */}
      <div>
        <p className={rowLabel}>
          {t('Font', 'შრიფტი')}
          <span className="font-normal text-[#4a3d66] dark:text-[#c3b8dd]/80">
            {value.font
              ? t('— your type', '— შენი შრიფტი')
              : t('— from the template', '— შაბლონის შრიფტი')}
          </span>
          {value.font && (
            <button
              type="button"
              onClick={() => onChange({ font: '' })}
              className={resetBtn}
              data-testid="invite-font-reset"
            >
              {t('Use template', 'შაბლონისა')}
            </button>
          )}
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {INVITE_FONTS.map((f) => {
            const on = font === f.id
            const isTemplateFont = f.id === defaults.font
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFont(f.id)}
                aria-pressed={on}
                data-testid={`invite-font-${f.id}`}
                className={`rounded-[4px] border px-2.5 py-2 text-left transition-all ${
                  on
                    ? 'border-[#ff4d8d] bg-[#ff4d8d]/[0.06]'
                    : 'border-[#1b1033]/12 hover:border-[#1b1033]/25 dark:border-white/10 dark:hover:border-white/25'
                }`}
              >
                <span
                  className="block truncate text-lg leading-tight text-[#1b1033] dark:text-[#f4f0ff]"
                  style={{ fontFamily: f.display }}
                >
                  {t('Anna', 'ანა')}
                </span>
                <span className="mt-0.5 flex items-center gap-1 text-[10px] leading-snug text-[#4a3d66] dark:text-[#c3b8dd]/80">
                  <span className="truncate">{appLang === 'ka' ? f.label.ka : f.label.en}</span>
                  {isTemplateFont && (
                    <span className="shrink-0 rounded-sm bg-[#1b1033]/[0.07] px-1 text-[9px] font-medium dark:bg-white/10">
                      {t('default', 'ნაგულისხმევი')}
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <PaintedPreviewDialog
        templateId={quickId}
        list={visible.filter((tpl) => tpl.painted)}
        words={hostWords || (quickId ? sampleWordsFor(getTemplate(quickId), invLang) : { title: '' })}
        lang={invLang}
        appLang={appLang}
        current={value}
        onClose={() => setQuickId(null)}
        onNavigate={setQuickId}
        onUse={(patch) => {
          onChange(patch)
          setQuickId(null)
        }}
      />
    </div>
  )
}

// Every template id the picker can emit — handy for tests and the gallery.
export const TEMPLATE_ID_LIST = INVITE_TEMPLATES.map((tpl) => tpl.id)

export default InviteTemplatePicker
